import React, { useEffect, useMemo, useState } from 'react';
import authService from '../utils/auth.js';
import '../styles/user-analytics.css';

const FILTERS = [
  { id: 'all', label: 'All users' },
  { id: 'attention', label: 'Needs attention' },
  { id: 'unverified', label: 'Unverified' },
  { id: 'never', label: 'Never logged in' },
  { id: 'inactive', label: 'Inactive 90+ days' },
  { id: 'reset', label: 'Pending reset' }
];

const FLAG_LABELS = {
  unverified: 'Unverified',
  never_logged_in: 'Never logged in',
  login_never_completed: 'Login not finished',
  inactive: 'Inactive',
  pending_reset: 'Pending reset',
  must_change_password: 'Must change password'
};

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatRelative(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const days = Math.floor((Date.now() - date.getTime()) / 86400000);
  if (days < 1) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} mo ago`;
  const years = Math.floor(days / 365);
  return `${years} yr ago`;
}

function displayName(person) {
  const name = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
  return name || person.email.split('@')[0];
}

function matchesFilter(person, filter) {
  if (filter === 'all') return true;
  if (filter === 'attention') return person.flags.length > 0;
  if (filter === 'unverified') return person.flags.includes('unverified');
  if (filter === 'never') return person.flags.includes('never_logged_in');
  if (filter === 'inactive') return person.flags.includes('inactive');
  if (filter === 'reset') return person.flags.includes('pending_reset');
  return true;
}

export default function UserAnalyticsPanel({ apiBaseUrl, isSuperAdmin, selectedTenant, embedded = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const url = isSuperAdmin
          ? selectedTenant === 'system'
            ? `${apiBaseUrl}/api/super-admin/user-analytics?systemOnly=true`
            : selectedTenant
              ? `${apiBaseUrl}/api/super-admin/user-analytics?tenantId=${encodeURIComponent(selectedTenant)}`
              : `${apiBaseUrl}/api/super-admin/user-analytics`
          : `${apiBaseUrl}/api/auth/user-analytics`;

        const response = await fetch(url, { headers: authService.getAuthHeaders() });
        const payload = await response.json().catch(() => ({}));

        if (response.status === 401 || authService.isSessionExpiredError({ status: response.status, message: payload.message })) {
          authService.redirectToLogin();
          return;
        }

        if (!response.ok || payload.status === 'error') {
          throw new Error(payload.message || 'Failed to load user analytics');
        }

        if (!cancelled) setData(payload.data || null);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load user analytics');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, isSuperAdmin, selectedTenant]);

  const people = data?.people || [];
  const summary = data?.summary || {};

  const visiblePeople = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return people.filter((person) => {
      if (!matchesFilter(person, filter)) return false;
      if (!needle) return true;
      const haystack = [person.email, person.firstName, person.lastName, person.role, person.companyId]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [people, filter, query]);

  const cards = [
    { id: 'all', label: 'Users', value: summary.total || 0 },
    { id: 'attention', label: 'Needs attention', value: summary.needsAttention || 0, warn: true },
    { id: 'unverified', label: 'Unverified', value: summary.unverified || 0, warn: true },
    { id: 'never', label: 'Never logged in', value: summary.neverLoggedIn || 0, warn: true },
    { id: 'inactive', label: 'Inactive 90+ days', value: summary.inactive90d || 0 },
    { id: 'reset', label: 'Pending reset', value: summary.pendingReset || 0 }
  ];

  return (
    <div className={`admin-section user-analytics-section ${embedded ? 'is-embedded' : ''}`}>
      {!embedded && (
        <div className="admin-section-header">
          <div>
            <h2 className="admin-section-title">Users analytics</h2>
            <p className="admin-section-subtitle">
              See who verified, who signed in, and which accounts have gone quiet.
            </p>
          </div>
        </div>
      )}

      {loading && (
        <div className="admin-loading admin-skeleton" role="status" aria-live="polite">
          <span className="admin-sr-only">Loading users analytics…</span>
          {[0, 1, 2, 3].map((i) => (
            <div className="admin-skeleton-row" key={i} aria-hidden="true">
              <span className="nb-skeleton" />
              <span className="nb-skeleton" />
              <span className="nb-skeleton" />
              <span className="nb-skeleton" />
            </div>
          ))}
        </div>
      )}
      {error && <div className="admin-message error">{error}</div>}

      {!loading && !error && (
        <>
          <div className="user-analytics-cards">
            {cards.map((card) => (
              <button
                key={card.id}
                type="button"
                className={`user-analytics-card ${filter === card.id ? 'is-active' : ''} ${card.warn && card.value ? 'is-warn' : ''}`}
                onClick={() => setFilter(card.id)}
              >
                <span className="user-analytics-card-value">{card.value}</span>
                <span className="user-analytics-card-label">{card.label}</span>
              </button>
            ))}
          </div>

          <div className="user-analytics-toolbar">
            <div className="user-analytics-filters">
              {FILTERS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`user-analytics-chip ${filter === item.id ? 'is-active' : ''}`}
                  onClick={() => setFilter(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <input
              type="search"
              className="user-analytics-search"
              placeholder="Search name or email"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>

          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Person</th>
                  {isSuperAdmin && <th>Tenant</th>}
                  <th>Verified</th>
                  <th>Last login</th>
                  <th>Created</th>
                  <th>Flags</th>
                </tr>
              </thead>
              <tbody>
                {visiblePeople.length === 0 ? (
                  <tr>
                    <td colSpan={isSuperAdmin ? 6 : 5} className="admin-empty">
                      No people match this filter.
                    </td>
                  </tr>
                ) : (
                  visiblePeople.map((person) => (
                    <tr key={person.id || person.email}>
                      <td>
                        <div className="admin-user-identity">
                          <div>
                            <div className="user-analytics-name">{displayName(person)}</div>
                            <div className="user-analytics-email">{person.email}</div>
                          </div>
                        </div>
                      </td>
                      {isSuperAdmin && <td>{person.tenantName || person.companyId || '—'}</td>}
                      <td>
                        {person.isEmailVerified ? (
                          <span className="user-analytics-status is-ok">
                            Verified
                            {person.emailVerifiedAt
                              ? ` · ${formatDate(person.emailVerifiedAt)}`
                              : ''}
                          </span>
                        ) : (
                          <span className="user-analytics-status is-bad">Not verified</span>
                        )}
                      </td>
                      <td>
                        {person.lastLogin ? (
                          <span>
                            {formatDate(person.lastLogin)}
                            <span className="user-analytics-relative"> · {formatRelative(person.lastLogin)}</span>
                          </span>
                        ) : (
                          <span className="user-analytics-status is-bad">Never</span>
                        )}
                      </td>
                      <td>{formatDate(person.createdAt)}</td>
                      <td>
                        <div className="user-analytics-flags">
                          {person.flags.length === 0 ? (
                            <span className="user-analytics-flag is-ok">Healthy</span>
                          ) : (
                            person.flags.map((flag) => (
                              <span key={flag} className="user-analytics-flag">
                                {FLAG_LABELS[flag] || flag}
                              </span>
                            ))
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
